import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { getMonthWiseDueList } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * Direct port of digicampusbd's `fee-report-month-wise-student-payable-due-list`
 * ("Fee Due List Student wise") — for each student in a section (+ group),
 * which fee-subheads become due in each selected calendar month (from
 * `FeeSubHeadTimeConfiguration`) and haven't been collected yet, split into
 * "not collected at all" and "invoiced but only partially paid" — the same
 * two-bucket total the old app itself computes. New backend method
 * (`FeeReportService.monthWisePayableDueDetails`) since this needed the
 * month-schedule lookup, unlike most fee-report variants which are pure
 * frontend groupings over already-exposed invoice data (see that service's
 * own doc comment).
 */
export default function FeeMonthWiseDueReportPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [months, setMonths] = useState([]);
  const [fromRoll, setFromRoll] = useState("");
  const [toRoll, setToRoll] = useState("");

  const [rows, setRows] = useState(null);
  const [monthKeys, setMonthKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    groupApi.list().then(setGroups);
  }, []);

  const toggleMonth = (m) => setMonths((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const handleSearch = async () => {
    setError("");
    setRows(null);
    if (!academicYear || !classConfigId) {
      setError("Please select academic year and section.");
      return;
    }
    setLoading(true);
    try {
      const data = await getMonthWiseDueList(academicYear, classConfigId, groupId || undefined, months.length > 0 ? months : MONTHS);
      const withTotals = data.filter((r) => r.grandTotal !== 0);
      if (withTotals.length === 0) {
        setError("No Due Found");
      }
      const keys = withTotals.length > 0 ? Object.keys(withTotals[0].dueDetails) : [];
      setMonthKeys(keys);
      setRows(withTotals);
    } catch (err) {
      setError("Failed to load due report.");
    } finally {
      setLoading(false);
    }
  };

  const visibleRows = (rows || []).filter((r) => {
    const from = fromRoll ? Number(fromRoll) : 0;
    const to = toRoll ? Number(toRoll) : Infinity;
    return r.studentRoll >= from && r.studentRoll <= to;
  });
  const grandTotal = visibleRows.reduce((sum, r) => sum + r.grandTotal, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Due List Student wise</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          Select Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">All groups</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="form-row">
        <span className="admit-card-label">Select Month:</span>{" "}
        {MONTHS.map((m) => (
          <label key={m} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 10 }}>
            <input type="checkbox" checked={months.includes(m)} onChange={() => toggleMonth(m)} />
            {m}
          </label>
        ))}
      </div>
      <div className="attendance-filters">
        <label>
          From Roll
          <input type="number" value={fromRoll} onChange={(e) => setFromRoll(e.target.value)} />
        </label>
        <label>
          To Roll
          <input type="number" value={toRoll} onChange={(e) => setToRoll(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>SL</th>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Roll</th>
                  {monthKeys.map((m) => (
                    <th key={m}>{m}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => (
                  <tr key={r.identificationId}>
                    <td>{i + 1}</td>
                    <td>{r.customStudentId}</td>
                    <td>{r.studentName}</td>
                    <td>{r.studentRoll}</td>
                    {monthKeys.map((m) => (
                      <td key={m}>{r.dueDetails[m]}</td>
                    ))}
                    <td>{r.grandTotal}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4 + monthKeys.length} style={{ fontWeight: 800, textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td>{grandTotal}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
